import { zodResolver } from "@hookform/resolvers/zod"
import * as Sentry from "@sentry/react"
import { createFileRoute, useRouter } from "@tanstack/react-router"
import { PermutLogo } from "assets/logo"
import axios, { AxiosError } from "axios"
import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import { CardDescription, CardTitle } from "design-system/molecules/Card"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { AuthBackground } from "modules/auth/components/AuthBackground"
import { useCompleteSignupByWebsiteMutation } from "modules/auth/hooks/useCompleteSignupByWebsiteMutation"
import { useAuth } from "modules/shared/hooks/useAuth"
import { useEffect, useRef } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { CompleteSignupByWebsiteSchema, Errors } from "schema"
import { z } from "zod"

export const Route = createFileRoute("/_base/_public/signup-website/preview")({
  component: SignupWebsitePreview,
  validateSearch: z.object({
    token: z.string(),
    slug: z.string(),
    companyName: z.string().optional().default(""),
    name: z.string().optional().default(""),
  }),
})

type FieldValues = z.infer<typeof CompleteSignupByWebsiteSchema>

function SignupWebsitePreview() {
  const { t } = useTranslation()
  const { token, slug, companyName: initialCompanyName, name: initialName } = Route.useSearch()
  const router = useRouter()
  const { onLogin } = useAuth()
  const { mutateAsync, isPending } = useCompleteSignupByWebsiteMutation()
  const iframeRef = useRef<HTMLIFrameElement | null>(null)

  const form = useForm<FieldValues>({
    resolver: zodResolver(CompleteSignupByWebsiteSchema),
    defaultValues: {
      setupToken: token,
      companyName: initialCompanyName,
      name: initialName,
      password: "",
      confirmPassword: "",
    },
  })

  useEffect(() => {
    const iframe = iframeRef.current
    if (!iframe) return

    axios
      .get(`/api/v1/widget/${slug}`)
      .then(({ data: response }) => {
        const config = response.data
        if (!config || !iframe.contentWindow) return

        const html = `<!DOCTYPE html><html>
<head>
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>body { margin: 0; padding: 0; }</style>
</head><body>
<script
  src="/widget.js"
  type="module"
  defer
  data-permut="${slug}"
  data-custom-styles=".main-element { background: transparent!important;}"
>
</script>
</body></html>`

        const doc = iframe.contentWindow?.document
        if (!doc) return

        doc.open()
        doc.write(html)
        doc.close()
      })
      .catch((err) => {
        Sentry.captureException(err)
      })
  }, [slug])

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    try {
      const response = await mutateAsync(data)
      onLogin(response, router)
    } catch (err) {
      Sentry.captureException(err)
      const errorTranslations = getErrorTranslations()
      const key: Errors =
        err instanceof AxiosError
          ? (err.response?.data?.error?.key ?? Errors.GENERIC_ERROR)
          : Errors.GENERIC_ERROR
      form.setError("root", { message: errorTranslations[key] })
    }
  }

  return (
    <div className="relative flex min-h-svh w-screen overflow-hidden">
      <AuthBackground />
      <div className="flex flex-col w-full lg:w-[45%] min-h-svh justify-center items-center p-8 bg-background/50 z-10 border-r border-background/50 shadow-[0px_0_160px_#00000010]">
        <div className="flex flex-col gap-8 w-full max-w-sm">
          <PermutLogo className="w-36 h-12" />

          <div className="flex flex-col gap-2">
            <CardTitle>{t("auth.signupWebsite.completeTitle")}</CardTitle>
            <CardDescription>{t("auth.signupWebsite.completeDescription")}</CardDescription>
          </div>

          <Form
            {...form}
            errorTranslations={getErrorTranslations()}
            onSubmit={form.handleSubmit(onSubmit)}
            className="w-full"
          >
            <div className="flex flex-col gap-4">
              <FormField
                control={form.control}
                name="companyName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("auth.signupWebsite.companyName.field")}</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder={t("auth.signupWebsite.companyName.placeholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("auth.name.field")}</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder={t("auth.name.placeholder")}
                        autoComplete="name"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("auth.password.field")}</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder={t("auth.password.placeholder")}
                        autoComplete="new-password"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("auth.confirmPassword.field")}</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder={t("auth.confirmPassword.placeholder")}
                        autoComplete="new-password"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription />
                    <FormMessage />
                  </FormItem>
                )}
              />
              {form.formState.errors.root && (
                <p className="text-sm text-destructive">{form.formState.errors.root.message}</p>
              )}
            </div>
            <div className="pt-4">
              <Button disabled={isPending || !form.formState.isValid} className="w-full">
                {t("auth.signupWebsite.completeSubmit")}
              </Button>
            </div>
          </Form>
        </div>
      </div>

      {/* Left panel – widget preview */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-muted/20 relative">
        <iframe
          ref={iframeRef}
          title="Widget preview"
          className="w-full h-full border-0"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        />
      </div>
    </div>
  )
}
